import {
  Objective,
  KeyResult,
  OkrCycle,
  PaginatedResponse,
  ObjectiveDto,
  KeyResultDto,
  EntityResponse,
  ICreateObjective,
  IUpdateObjective,
  ICreateKeyResult,
  IUpdateKeyResult,
  ObjectiveFilter,
  ObjectiveReorderItem,
  KeyResultReorderItem,
  KeyResultFilter,
} from "@/types";
import { apiClient } from "@/lib/apiClient";
import type { KeyResultStatus, ObjectiveStatus } from "@/types";
import {
  parseKeyResult,
  parseKeyResultPage,
  parseObjective,
  parseObjectivePage,
} from "@/services/okrTransforms";

/** Keep `ownerUserPid` aligned with the BE DTOs: omit an empty string, keep `null` (explicit clear). */
function normalizeOwnerPayload(payload: Record<string, unknown>) {
  const v = payload.ownerUserPid;
  if (v === "") {
    delete payload.ownerUserPid;
  }
}

export const okrService = {
  // ── Objectives ────────────────────────────────
  getObjectives: async (params?: ObjectiveFilter): Promise<PaginatedResponse<Objective>> => {
    const queryParams: Record<string, string> = {
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
    };
    if (params?.search) queryParams.search = params.search;
    if (params?.statuses) {
      const raw = Array.isArray(params.statuses) ? params.statuses : [params.statuses];
      queryParams.statuses = raw.map((s) => String(s as ObjectiveStatus | KeyResultStatus)).join(",");
    }
    if (params?.cyclePid) queryParams.cyclePid = params.cyclePid;
    if (params?.category) queryParams.category = params.category;
    if (params?.ownerUserPid && params.ownerUserPid !== "all")
      queryParams.ownerUserPid = params.ownerUserPid;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const res = await apiClient.get<{ data?: ObjectiveDto[]; total?: number }>("/api/objectives", {
      params: queryParams,
    });
    return parseObjectivePage(res);
  },

  getObjective: async (pid: string): Promise<Objective> => {
    const res = await apiClient.get<ObjectiveDto | EntityResponse<ObjectiveDto>>(`/api/objectives/${pid}`);
    return parseObjective(res);
  },

  createObjective: async (data: ICreateObjective): Promise<Objective> => {
    const payload = { ...data } as Record<string, unknown>;
    normalizeOwnerPayload(payload);
    const res = await apiClient.post<ObjectiveDto | EntityResponse<ObjectiveDto>>("/api/objectives", payload);
    return parseObjective(res);
  },

  updateObjective: async (pid: string, data: IUpdateObjective): Promise<Objective> => {
    const payload = { ...data } as Record<string, unknown>;
    if (data.status != null) {
      payload.status = data.status;
    }
    normalizeOwnerPayload(payload);
    const res = await apiClient.put<ObjectiveDto | EntityResponse<ObjectiveDto>>(`/api/objectives/${pid}`, payload);
    return parseObjective(res);
  },

  deleteObjective: async (pid: string): Promise<void> => {
    return apiClient.delete<void>(`/api/objectives/${pid}`);
  },

  reorderObjectives: async (reorderData: ObjectiveReorderItem[]): Promise<void> => {
    return apiClient.post<void>("/api/objectives/reorder", { items: reorderData });
  },

  // ── Key Results ───────────────────────────────
  getKeyResults: async (params?: KeyResultFilter): Promise<PaginatedResponse<KeyResult>> => {
    const queryParams: Record<string, string> = {
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
    };
    if (params?.search) queryParams.search = params.search;
    if (params?.statuses) {
      const raw = Array.isArray(params.statuses) ? params.statuses : [params.statuses];
      queryParams.statuses = raw.map((s) => String(s as ObjectiveStatus | KeyResultStatus)).join(",");
    }
    if (params?.objectivePid) queryParams.objectivePid = params.objectivePid;
    if (params?.ownerUserPid && params.ownerUserPid !== "all")
      queryParams.ownerUserPid = params.ownerUserPid;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const res = await apiClient.get<{ data?: KeyResultDto[]; total?: number }>("/api/key-results", {
      params: queryParams,
    });
    return parseKeyResultPage(res);
  },

  getKeyResult: async (pid: string): Promise<KeyResult> => {
    const res = await apiClient.get<KeyResultDto | EntityResponse<KeyResultDto>>(`/api/key-results/${pid}`);
    return parseKeyResult(res);
  },

  createKeyResult: async (data: ICreateKeyResult): Promise<KeyResult> => {
    const payload = { ...data } as Record<string, unknown>;
    normalizeOwnerPayload(payload);
    const res = await apiClient.post<KeyResultDto | EntityResponse<KeyResultDto>>("/api/key-results", payload);
    return parseKeyResult(res);
  },

  updateKeyResult: async (pid: string, data: IUpdateKeyResult): Promise<KeyResult> => {
    const payload = { ...data } as Record<string, unknown>;
    if (data.status != null) {
      payload.status = data.status;
    }
    normalizeOwnerPayload(payload);
    const res = await apiClient.put<KeyResultDto | EntityResponse<KeyResultDto>>(`/api/key-results/${pid}`, payload);
    return parseKeyResult(res);
  },

  deleteKeyResult: async (pid: string): Promise<void> => {
    return apiClient.delete<void>(`/api/key-results/${pid}`);
  },

  reorderKeyResults: async (reorderData: KeyResultReorderItem[]): Promise<void> => {
    return apiClient.post<void>("/api/key-results/reorder", { items: reorderData });
  },

  getOkrCycles: async (): Promise<OkrCycle[]> => {
    const res = await apiClient.get<OkrCycle[] | { data: OkrCycle[] }>("/api/okr-cycles");
    return Array.isArray(res) ? res : (res as { data: OkrCycle[] }).data ?? [];
  },
};
